/**
 * Authentication store using Pinia.
 *
 * Manages user authentication state, login/logout, and JWT token persistence.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { getErrorMessage } from '@/composables/useApi'
import type {
  User,
  Tenant,
  LoginRequest,
  RegisterRequest,
  RegisterResponse,
  TokenResponse,
  UserProfile,
} from '@/types/auth'

const TOKEN_KEY = 'auth_token'
const USER_PROFILE_KEY = 'user_profile'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const tenant = ref<Tenant | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => {
    if (!user.value) return false
    return ['super_admin', 'tenant_admin'].includes(user.value.role)
  })
  const canWrite = computed(() => {
    if (!user.value) return false
    return ['super_admin', 'tenant_admin', 'recruiter'].includes(user.value.role)
  })

  // Actions

  /**
   * Initialize store from localStorage.
   */
  function init() {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedProfile = localStorage.getItem(USER_PROFILE_KEY)

    if (storedToken && storedProfile) {
      try {
        const profile: UserProfile = JSON.parse(storedProfile)
        token.value = storedToken
        user.value = profile.user
        tenant.value = profile.tenant
      } catch (e) {
        console.error('Failed to parse stored profile:', e)
        logout()
      }
    }
  }

  /**
   * Login user.
   */
  async function login(credentials: LoginRequest): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await api.post<TokenResponse>('/auth/login', credentials)
      const tokenData = response.data

      // Save token
      token.value = tokenData.access_token
      localStorage.setItem(TOKEN_KEY, tokenData.access_token)

      // Fetch user profile
      await fetchMe()
    } catch (e) {
      error.value = getErrorMessage(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Register new tenant and admin user.
   */
  async function register(data: RegisterRequest): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await api.post<RegisterResponse>('/auth/register', data)
      const registerData = response.data

      // Save token
      token.value = registerData.token.access_token
      localStorage.setItem(TOKEN_KEY, registerData.token.access_token)

      // Save user and tenant
      user.value = registerData.user
      tenant.value = registerData.tenant

      // Persist profile
      const profile: UserProfile = {
        user: registerData.user,
        tenant: registerData.tenant,
      }
      localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile))
    } catch (e) {
      error.value = getErrorMessage(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch current user profile.
   */
  async function fetchMe(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await api.get<UserProfile>('/auth/me')
      const profile = response.data

      user.value = profile.user
      tenant.value = profile.tenant

      // Persist profile
      localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile))
    } catch (e) {
      error.value = getErrorMessage(e)
      // If fetchMe fails, logout
      logout()
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Refresh access token.
   */
  async function refreshToken(refreshToken: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await api.post<TokenResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      })
      const tokenData = response.data

      token.value = tokenData.access_token
      localStorage.setItem(TOKEN_KEY, tokenData.access_token)
    } catch (e) {
      error.value = getErrorMessage(e)
      logout()
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Logout user.
   */
  function logout() {
    user.value = null
    tenant.value = null
    token.value = null
    error.value = null

    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_PROFILE_KEY)
  }

  /**
   * Clear error message.
   */
  function clearError() {
    error.value = null
  }

  return {
    // State
    user,
    tenant,
    token,
    loading,
    error,

    // Computed
    isAuthenticated,
    isAdmin,
    canWrite,

    // Actions
    init,
    login,
    register,
    fetchMe,
    refreshToken,
    logout,
    clearError,
  }
})
